import Pagination from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { courseService } from '@/services/course.service';
import { courseAssignmentService } from '@/services/course-assignment.service';
import { courseAssignmentAnalyticsService } from '@/services/courseAssignmentAnalytics.service';
import { bulkImportHistoryService } from '@/services/bulkImportHistory.service';
import { userService } from '@/services/user.service';
import { useQuery } from '@tanstack/react-query';
import type {
  Course,
  OverviewAnalytics,
  AssignmentListResponse,
  AssignmentCourse
} from '@/types/courseAssignmentAnalytics.types';
import type {
  BulkImportHistoryResponse,
  BulkImportHistoryItem
} from '@/types/bulkImportHistory.types';
import {
  Activity,
  AlertTriangle,
  BookOpen,
  Calendar,
  CheckCircle,
  ExternalLink,
  Eye,
  History,
  List,
  Mail,
  Package,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  TrendingUp,
  User,
  Users,
  XCircle
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/Toast';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/utils/debounce';
import { useForm } from 'react-hook-form';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_ASSIGNMENT_ANALYTICS_PERMISSION, COURSE_ASSIGNMENT_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_COURSE_ASSIGNMENT } from '@/constants/constant-routes';

interface FilterFormData {
  selectedCourse: string;
  selectedGranularity: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  bulkHistoryStatusFilter: string;
}

export default function CourseAssignmentAnalytics() {
  usePermissionRouteAccess([
    COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_READ,
    COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_WRITE,
  ]);
  const canWrite = useHasPermission([COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_WRITE]);
  const canAssign = useHasPermission([COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_WRITE]);

  const [activeTab, setActiveTab] = useState<'users' | 'groups' | 'roles' | 'bulk-history'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm); // Debounced search
  const [filterStatus] = useState<string>('all');

  // Initialize react-hook-form
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: {
      selectedCourse: 'all',
      selectedGranularity: 'monthly',
      bulkHistoryStatusFilter: 'all'
    }
  });

  // Watch form values
  const selectedCourse = watch('selectedCourse');
  const selectedGranularity = watch('selectedGranularity');
  const bulkHistoryStatusFilter = watch('bulkHistoryStatusFilter');
  const [, setLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [userTableLoading, setUserTableLoading] = useState(false);
  const [groupTableLoading, setGroupTableLoading] = useState(false);
  const [roleTableLoading, setRoleTableLoading] = useState(false);
  const [bulkHistoryLoading, setBulkHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [, setOpenActionMenu] = useState<string | null>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<BulkImportHistoryItem | null>(null);
  const [selectedCoursesModal, setSelectedCoursesModal] = useState<{courses: AssignmentCourse[], assignmentId: string} | null>(null);
  const [selectedAssignedUsersModal, setSelectedAssignedUsersModal] = useState<{
    users?: {_id: string, name: string, email: string}[],
    groups?: {group_key: string, group_name: string, user_count: number}[],
    roles?: {role_key: string, role_name: string, user_count: number}[],
    type: 'USER' | 'GROUP' | 'ROLE'
  } | null>(null);
  const [selectedSkippedUsersModal, setSelectedSkippedUsersModal] = useState<{
    skipped_users?: {user_id: string, user_email: string, user_name: string, course_id: string, reason: string}[],
    skipped_groups?: {group_key: string, group_name: string, course_id: string, reason: string}[],
    skipped_roles?: {group_key: string, role_key: string, role_name: string, course_id: string, reason: string}[]
  } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [revokeModal, setRevokeModal] = useState<{ assignmentId: string; type: 'GROUP' | 'ROLE'; name: string } | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [, setSearchParams] = useSearchParams();
  // Data states
  const [overviewMetrics, setOverviewMetrics] = useState<OverviewAnalytics | null>(null);
  const [userAssignments, setUserAssignments] = useState<AssignmentListResponse | null>(null);
  const [groupAssignments, setGroupAssignments] = useState<AssignmentListResponse | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<AssignmentListResponse | null>(null);
  const [bulkImportHistory, setBulkImportHistory] = useState<BulkImportHistoryResponse | null>(null);
  const [, setCourses] = useState<Course[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
  const params = new URLSearchParams();
  if (searchTerm) params.set('search', searchTerm);
  if (selectedCourse && selectedCourse !== 'all') params.set('course', selectedCourse);
  setSearchParams(params, { replace: true });
}, [searchTerm, selectedCourse, setSearchParams]);

  // Fetch course dropdown data
  const { data: courseDropdownData } = useQuery({
    queryKey: ['course-dropdown'],
    queryFn: () => userService.getCourseDropdown(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  const courseOptions = courseDropdownData?.data || [];

  // Load initial data on mount - fetch all assignment types
  useEffect(() => {
    loadCourses();
    loadBulkImportHistory();
    loadOverviewAnalytics();
    // Load all three assignment types on initial mount
    loadUserAssignments();
    loadGroupAssignments();
    loadRoleAssignments();
  }, []);

  // Load overview analytics when granularity changes
  useEffect(() => {
    loadOverviewAnalytics();
  }, [selectedGranularity]);

  // Reload data when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'users') {
      loadUserAssignments();
    } else if (activeTab === 'groups') {
      loadGroupAssignments();
    } else if (activeTab === 'roles') {
      loadRoleAssignments();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, filterStatus, selectedCourse]);

  // Load bulk import history
  useEffect(() => {
    if (activeTab === 'bulk-history') {
      setBulkImportHistory(null);
      loadBulkImportHistory();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, bulkHistoryStatusFilter]);

  // Reset to first page when search changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm && searchTerm !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverviewAnalytics(),
        loadUserAssignments(),
        loadGroupAssignments(),
        loadRoleAssignments(),
        loadCourses(),
      ]);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTabData = async () => {
    try {
      // Load all tab data in parallel in the background
      await Promise.all([
        loadUserAssignments(),
        loadGroupAssignments(),
        loadRoleAssignments(),
        loadBulkImportHistory()
      ]);
    } catch (error) {
      console.error('Error loading tab analytics data:', error);
    }
  };

  const loadOverviewAnalytics = async () => {
    setOverviewLoading(true);
    try {
      const data = await courseAssignmentAnalyticsService.getOverviewAnalytics(selectedGranularity);
      setOverviewMetrics(data);
    } catch (error: any) {
      toast.error('Failed to load overview metrics');
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await courseService.getCourses();
      setCourses(response.courses || []);
    } catch (error: any) {
      console.error('Failed to load courses:', error);
    }
  };

  const loadUserAssignments = async () => {
    setUserTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'USER',
        status: 'ACTIVE'
      };

      // Add search parameter if searchTerm is not empty
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add course_id parameter if a course is selected
      if (selectedCourse && selectedCourse !== 'all') {
        params.course_id = selectedCourse;
      }

      const response: any = await courseAssignmentService.listAssignments(params);

      console.log('User Assignments Response:', response);

      // The API returns the data directly in the correct format
      setUserAssignments(response);
    } catch (error: any) {
      console.error('Error loading user assignments:', error);
      toast.error('Failed to load user assignments');
    } finally {
      setUserTableLoading(false);
    }
  };

  const loadGroupAssignments = async () => {
    setGroupTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'GROUP',
        status: 'ACTIVE'
      };

      // Add search parameter if searchTerm is not empty
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add course_id parameter if a course is selected
      if (selectedCourse && selectedCourse !== 'all') {
        params.course_id = selectedCourse;
      }

      const response: any = await courseAssignmentService.listAssignments(params);

      console.log('Group Assignments Response:', response);

      // The API returns the data directly in the correct format
      setGroupAssignments(response);
    } catch (error: any) {
      console.error('Error loading group assignments:', error);
      toast.error('Failed to load group assignments');
    } finally {
      setGroupTableLoading(false);
    }
  };

  const loadRoleAssignments = async () => {
    setRoleTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'ROLE',
        status: 'ACTIVE'
      };

      // Add search parameter if searchTerm is not empty
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add course_id parameter if a course is selected
      if (selectedCourse && selectedCourse !== 'all') {
        params.course_id = selectedCourse;
      }

      const response: any = await courseAssignmentService.listAssignments(params);

      console.log('Role Assignments Response:', response);

      // The API returns the data directly in the correct format
      setRoleAssignments(response);
    } catch (error: any) {
      console.error('Error loading role assignments:', error);
      toast.error('Failed to load role assignments');
    } finally {
      setRoleTableLoading(false);
    }
  };

  const loadBulkImportHistory = async () => {
    setBulkHistoryLoading(true);
    try {
      const data = await bulkImportHistoryService.getBulkImportHistory({
        page: currentPage,
        limit: pageSize,
        ...(debouncedSearchTerm && { search_identifier: debouncedSearchTerm }),
        ...(bulkHistoryStatusFilter !== 'all' && { status: bulkHistoryStatusFilter as any }),
        sort_by: 'import_date',
        sort_order: 'desc'
      });
      setBulkImportHistory(data);
    } catch (error: any) {
      toast.error('Failed to load bulk import history');
    } finally {
      setBulkHistoryLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadAllData(),
      loadAllTabData()
    ]);
    setRefreshing(false);
    toast.success('Data refreshed successfully');
  };

  const handleRevokeAssignment = async () => {
    if (!revokeModal) return;
    setRevokeLoading(true);
    try {
      const result = await courseAssignmentService.revokeAssignment(revokeModal.assignmentId);
      toast.success(
        `Revoked ${revokeModal.type === 'GROUP' ? 'department' : 'role'} assignment. Deleted: ${result.course_trackings_deleted} tracking(s), ${result.assessment_attempts_deleted} attempt(s), ${result.certificates_deleted} certificate(s).${result.lp_linked_tracks_skipped > 0 ? ` Skipped ${result.lp_linked_tracks_skipped} LP-linked track(s).` : ''}`
      );
      setRevokeModal(null);
      // Refresh the current tab data
      if (activeTab === 'groups') loadGroupAssignments();
      else if (activeTab === 'roles') loadRoleAssignments();
    } catch (error: any) {
      const msg = error.response?.data?.error?.error_description || error.message || 'Failed to revoke assignment';
      toast.error(msg);
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleSendReminder = async (assignmentId: string) => {
    try {
      setActionLoading(assignmentId);

      const reminderData = await courseAssignmentService.sendReminderNotifications(assignmentId);

      const { message, failure_count } = reminderData;

      if (failure_count === 0) {
        toast.success(message);
      } else {
        toast.warning(message);
      }

    } catch (error: any) {
      console.error('Failed to send reminders:', error);
      toast.error(error.message || 'Failed to send reminders');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Gradient Header with Metrics */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-2">
              <BookOpen className="w-6 h-6 md:w-7 md:h-7" />
              Course Assignment Analytics
            </h1>
            <p className="text-brand-indigo-100 text-sm md:text-base">
              Track and analyze course assignments across users, groups, and roles
            </p>
          </div>
          <div className="flex gap-2 md:gap-3 flex-wrap">
            <div className="w-48">
              <Select<FilterFormData>
                control={control}
                name="selectedGranularity"
                options={[
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'yearly', label: 'Yearly' }
                ]}
                placeholder="Select Granularity"
                isClearable={false}
                isSearchable={false}
              />
            </div>
            {/* <button
              onClick={handleExport}
              disabled={exportLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading ? 'Exporting...' : 'Export'}
              </span>
            </button> */}
            <button
              onClick={() => window.location.href = ADMIN_COURSE_ASSIGNMENT}
              disabled={!canAssign}
              title={!canAssign ? 'You do not have permission to perform this action' : undefined}
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white text-brand-indigo-600 rounded-lg hover:bg-brand-indigo-50 transition-colors font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Users className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">Assign Course</span>
              <span className="sm:hidden">Assign</span>
            </button>
          </div>
        </div>

        {/* Key Metrics in Header */}
        {overviewLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-3 bg-white/20 rounded w-20 mb-2"></div>
                    <div className="h-8 bg-white/20 rounded w-16 mb-2"></div>
                    <div className="h-3 bg-white/20 rounded w-24"></div>
                  </div>
                  <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : overviewMetrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-indigo-100 text-xs">Total Assigned</p>
                  <p className="text-2xl font-bold">{overviewMetrics.totalAssigned}</p>
                  <p className="text-brand-indigo-200 text-xs mt-1">
                    <span className={overviewMetrics.periodComparison.totalAssigned.change.startsWith('+') ? 'text-green-300' : 'text-red-300'}>
                      {overviewMetrics.periodComparison.totalAssigned.change}
                    </span> vs last period
                  </p>
                </div>
                <BookOpen className="w-8 h-8 text-brand-indigo-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-indigo-100 text-xs">Completion Rate</p>
                  <p className="text-2xl font-bold">{overviewMetrics.completionRate}%</p>
                  <p className="text-brand-indigo-200 text-xs mt-1">
                    <span className={overviewMetrics.periodComparison.completionRate.change.startsWith('+') ? 'text-green-300' : 'text-red-300'}>
                      {overviewMetrics.periodComparison.completionRate.change}
                    </span> improvement
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-brand-indigo-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-indigo-100 text-xs">Avg. Progress</p>
                  <p className="text-2xl font-bold">{overviewMetrics.avgProgress}%</p>
                  <p className="text-brand-indigo-200 text-xs mt-1">
                    {formatTime(overviewMetrics.avgTimeSpent)} avg time
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-brand-indigo-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-indigo-100 text-xs">Engagement Score</p>
                  <p className="text-2xl font-bold">{overviewMetrics.engagementScore}%</p>
                  <p className="text-brand-indigo-200 text-xs mt-1">
                    <span className="text-yellow-300">{overviewMetrics.onTimeRate}%</span> on-time
                  </p>
                </div>
                <Activity className="w-8 h-8 text-brand-indigo-200" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max md:min-w-0">
            {[
              { key: 'users', label: 'User Assignments', icon: User, count: userAssignments?.meta_data.total_records || 0 },
              { key: 'groups', label: 'Department Assignments', icon: Users, count: groupAssignments?.meta_data.total_records || 0 },
              { key: 'roles', label: 'Role Assignments', icon: Shield, count: roleAssignments?.meta_data.total_records || 0 },
              { key: 'bulk-history', label: 'Bulk Import History', icon: History, count: bulkImportHistory?.pagination.total || 0 }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key as any);
                  setCurrentPage(1);
                  setSearchTerm(''); // Reset search when switching tabs
                }}
                className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-3 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-brand-indigo-600 text-brand-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                <span className="px-1.5 md:px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
       <div className="p-4 sm:p-6 border-b border-gray-200">
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
    <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder={
                  activeTab === 'users' ? 'Search by user name,email or employee ID...' :
                  activeTab === 'groups' ? 'Search by group name...' :
                  activeTab === 'roles' ? 'Search by role name...' :
                  'Search by email...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 text-sm"
              />
            </div>
            {(activeTab === 'users' || activeTab === 'groups' || activeTab === 'roles') && (
              <div className="w-full sm:w-auto sm:min-w-[200px]">
                <Select<FilterFormData>
                  control={control}
                  name="selectedCourse"
                  options={[
                    { value: 'all', label: 'All Courses' },
                    ...courseOptions.map((course: any) => {
                      const courseId = course._id || course.id;
                      const courseTitle = course.title || course.name || 'Unknown';
                      return {
                        value: courseId,
                        label: courseTitle
                      };
                    })
                  ]}
                  placeholder="Select Course"
                  isClearable={false}
                  onChange={() => {
                    console.log('Course dropdown changed to:', selectedCourse);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
            {activeTab === 'bulk-history' ? (
              <div className="w-full sm:w-48">
                <Select<FilterFormData>
                  control={control}
                  name="bulkHistoryStatusFilter"
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'PROCESSING', label: 'Processing' },
                    { value: 'FAILED', label: 'Failed' }
                  ]}
                  placeholder="Select Status"
                  isClearable={false}
                  isSearchable={false}
                  onChange={() => {
                    setCurrentPage(1);
                  }}
                />
              </div>
            ) : (
              // <select
              //   value={filterStatus}
              //   onChange={(e) => {
              //     setFilterStatus(e.target.value);
              //     setCurrentPage(1);
              //   }}
              //   className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500"
              // >
              //   <option value="all">All Status</option>
              //   <option value="not_started">Not Started</option>
              //   <option value="in_progress">In Progress</option>
              //   <option value="completed">Completed</option>
              //   <option value="overdue">Overdue</option>
              // </select>
              <></>
            )}
            <button
              onClick={handleRefresh}
              className={`p-2 text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 flex-shrink-0 ${
                refreshing ? 'animate-spin' : ''
              }`}
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'users' && (
          <div className="p-4 sm:p-6">
            {/* User Assignments Table */}
            {userTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : userAssignments && userAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline Days Count</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {userAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.users.slice(0, 1).map((user) => (
                                <div key={user._id} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-indigo-500 to-brand-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    {user.name ? user.name.split(' ').map(n => n[0]).join('') : '?'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</p>
                                    <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.users.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    users: assignment.assigned_to_details.users,
                                    type: 'USER'
                                  })}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.users.length - 1} more users
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.courses[0]?.title || 'N/A'}
                              </div>
                              {assignment.courses.length > 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline"
                                >
                                  +{assignment.courses.length - 1} more
                                </button>
                              )}
                              {assignment.courses.length === 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-indigo-600 transition-colors"
                                  title="View course details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {/* Check if there are any skipped items */}
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {!(assignment as any).skipped_users?.length && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users or groups */}
                                  {!(assignment as any).skipped_users?.length && !(assignment as any).skipped_groups?.length && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button if there are multiple skipped items */}
                                  {(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) > 1 && (
                                    <button
                                      onClick={() => setSelectedSkippedUsersModal({
                                        skipped_users: (assignment as any).skipped_users,
                                        skipped_groups: (assignment as any).skipped_groups,
                                        skipped_roles: (assignment as any).skipped_roles
                                      })}
                                      className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline ml-10"
                                    >
                                      +{(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) - 1} more skipped
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details?.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details?.email || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {assignment.deadline_days
                              ? `${assignment.deadline_days} ${assignment.deadline_days === '1' ? 'day' : 'days'}`
                              : 'N/A'}
                          </div>                          
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleSendReminder(assignment._id)}
                              disabled={!canWrite || actionLoading === assignment._id}
                              title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === assignment._id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                  <span>Sending...</span>
                                </>
                              ) : (
                                <>
                                  <Mail className="w-4 h-4" />
                                  <span>Reminder</span>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {userAssignments.meta_data.total_records > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(userAssignments.meta_data.total_records / pageSize)}
                      totalItems={userAssignments.meta_data.total_records}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <User className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-500">No user assignments found</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="p-4 sm:p-6">
            {/* Group Assignment Table */}
            {groupTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : groupAssignments && groupAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Groups</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline Days Count</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {groupAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.groups.slice(0, 1).map((group) => (
                                <div key={group.group_key} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    <Users className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{group.group_name}</p>
                                    <p className="text-xs text-gray-500">{group.user_count} members</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.groups.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    groups: assignment.assigned_to_details.groups,
                                    type: 'GROUP'
                                  })}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.groups.length - 1} more groups
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.courses[0]?.title || 'N/A'}
                              </div>
                              {assignment.courses.length > 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline"
                                >
                                  +{assignment.courses.length - 1} more
                                </button>
                              )}
                              {assignment.courses.length === 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-indigo-600 transition-colors"
                                  title="View course details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {/* Check if there are any skipped items */}
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {!(assignment as any).skipped_users?.length && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users or groups */}
                                  {!(assignment as any).skipped_users?.length && !(assignment as any).skipped_groups?.length && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button if there are multiple skipped items */}
                                  {(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) > 1 && (
                                    <button
                                      onClick={() => setSelectedSkippedUsersModal({
                                        skipped_users: (assignment as any).skipped_users,
                                        skipped_groups: (assignment as any).skipped_groups,
                                        skipped_roles: (assignment as any).skipped_roles
                                      })}
                                      className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline ml-10"
                                    >
                                      +{(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) - 1} more skipped
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details?.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details?.email || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                            {assignment.deadline_days
                              ? `${assignment.deadline_days} ${assignment.deadline_days === '1' ? 'day' : 'days'}`
                              : 'N/A'}
                          </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSendReminder(assignment._id)}
                                disabled={!canWrite || actionLoading === assignment._id}
                                title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {actionLoading === assignment._id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                    <span>Sending...</span>
                                  </>
                                ) : (
                                  <>
                                    <Mail className="w-4 h-4" />
                                    <span>Reminder</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => setRevokeModal({
                                  assignmentId: assignment._id,
                                  type: 'GROUP',
                                  name: assignment.assigned_to_details.groups?.[0]?.group_name || 'this department'
                                })}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to perform this action' : 'Revoke assignment'}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Revoke</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {groupAssignments.meta_data.total_records > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(groupAssignments.meta_data.total_records / pageSize)}
                      totalItems={groupAssignments.meta_data.total_records}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <Users className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-500">No group assignments found</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="p-4 sm:p-6">
            {/* Role Assignment Table */}
            {roleTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : roleAssignments && roleAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Roles</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline Days Count</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {roleAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.roles.slice(0, 1).map((role) => (
                                <div key={role.role_key} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    <Shield className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{role.role_name}</p>
                                    <p className="text-xs text-gray-500">{role.user_count} users</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.roles.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    roles: assignment.assigned_to_details.roles,
                                    type: 'ROLE'
                                  })}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.roles.length - 1} more roles
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.courses[0]?.title || 'N/A'}
                              </div>
                              {assignment.courses.length > 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="text-xs text-brand-indigo-600 font-medium hover:text-brand-indigo-800 hover:underline"
                                >
                                  +{assignment.courses.length - 1} more
                                </button>
                              )}
                              {assignment.courses.length === 1 && (
                                <button
                                  onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-indigo-600 transition-colors"
                                  title="View course details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {/* Check if there are any skipped items */}
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {!(assignment as any).skipped_users?.length && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users or groups */}
                                  {!(assignment as any).skipped_users?.length && !(assignment as any).skipped_groups?.length && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button if there are multiple skipped items */}
                                  {(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) > 1 && (
                                    <button
                                      onClick={() => setSelectedSkippedUsersModal({
                                        skipped_users: (assignment as any).skipped_users,
                                        skipped_groups: (assignment as any).skipped_groups,
                                        skipped_roles: (assignment as any).skipped_roles
                                      })}
                                      className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline ml-10"
                                    >
                                      +{(((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0)) - 1} more skipped
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details?.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details?.email || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                            {assignment.deadline_days
                              ? `${assignment.deadline_days} ${assignment.deadline_days === '1' ? 'day' : 'days'}`
                              : 'N/A'}
                          </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSendReminder(assignment._id)}
                                disabled={!canWrite || actionLoading === assignment._id}
                                title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {actionLoading === assignment._id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                    <span>Sending...</span>
                                  </>
                                ) : (
                                  <>
                                    <Mail className="w-4 h-4" />
                                    <span>Reminder</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => setRevokeModal({
                                  assignmentId: assignment._id,
                                  type: 'ROLE',
                                  name: assignment.assigned_to_details.roles?.[0]?.role_name || 'this role'
                                })}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to perform this action' : 'Revoke assignment'}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Revoke</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {roleAssignments.meta_data.total_records > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(roleAssignments.meta_data.total_records / pageSize)}
                      totalItems={roleAssignments.meta_data.total_records}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <Shield className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-500">No role assignments found</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bulk Import History Tab */}
        {activeTab === 'bulk-history' && (
          <div className="p-4 sm:p-6">
            {bulkHistoryLoading ? (
              /* Skeleton Loader */
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-1/2 sm:w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 sm:w-1/3"></div>
                      </div>
                      <div className="h-6 w-20 sm:w-24 bg-gray-200 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : bulkImportHistory && bulkImportHistory.data && bulkImportHistory.data.length > 0 ? (
              <>
                <div className="space-y-4 mb-6">
                  {bulkImportHistory.data.map((item) => {
                    const statusBgClass =
                      item.status === 'COMPLETED' ? 'bg-green-50' :
                      item.status === 'PROCESSING' ? 'bg-blue-50' : 'bg-red-50';

                    const statusIconClass =
                      item.status === 'COMPLETED' ? 'text-green-600' :
                      item.status === 'PROCESSING' ? 'text-blue-600' : 'text-red-600';

                    const statusBadgeClass =
                      item.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
                      item.status === 'PROCESSING' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700';

                    const successRate = item.total_records > 0
                      ? ((item.success_count / item.total_records) * 100).toFixed(1)
                      : '0';

                    return (
                      <div key={item._id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="p-4 sm:p-6">
                          {/* Header */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                            <div className="flex-1">
                              <div className="flex items-start gap-3 mb-2">
                                <div className={`p-2 rounded-lg ${statusBgClass} flex-shrink-0`}>
                                  <Package className={`w-4 h-4 sm:w-5 sm:h-5 ${statusIconClass}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                                    <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="truncate">{item.imported_by}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-gray-500">
                                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="truncate">{new Date(item.import_date).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${statusBadgeClass} self-start`}>
                              {item.status}
                            </span>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                                <List className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-xs text-gray-500">Total</span>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-gray-900">{item.total_records}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                <span className="text-xs text-green-600">Success</span>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-green-700">{item.success_count}</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                                <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 flex-shrink-0" />
                                <span className="text-xs text-red-600">Failed</span>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-red-700">{item.failed_count}</p>
                            </div>
                            <div className="bg-brand-indigo-50 rounded-lg p-2 sm:p-3">
                              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-brand-indigo-600 flex-shrink-0" />
                                <span className="text-xs text-brand-indigo-600">Rate</span>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-brand-indigo-700">{successRate}%</p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-700">Progress</span>
                              <span className="text-xs sm:text-sm text-gray-500">
                                {item.success_count} / {item.total_records}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${successRate}%` }}
                              />
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => setSelectedHistoryItem(item)}
                              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors text-xs sm:text-sm font-medium"
                            >
                              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">View Details</span>
                              <span className="sm:hidden">Details</span>
                            </button>
                            {/* {item.failed_count > 0 && (
                              <button className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                                <AlertCircle className="w-4 h-4" />
                                View Errors ({item.failed_count})
                              </button>
                            )} */}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {bulkImportHistory.pagination.total > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={bulkImportHistory.pagination.total_pages}
                    totalItems={bulkImportHistory.pagination.total}
                    itemsPerPage={pageSize}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setPageSize}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                    <History className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Import History</h3>
                  <p className="text-gray-500 mb-4">You haven't performed any bulk imports yet</p>
                  {/* <button
                    onClick={() => window.location.href = ADMIN_COURSE_ASSIGNMENT}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Start Bulk Import
                  </button> */}
                </div>
              </div>
            )}

            {/* Detail Modal */}
            {selectedHistoryItem && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                {/* Column layout: header and footer keep their height, only the content scrolls */}
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="flex-shrink-0 flex items-center justify-between p-4 sm:p-6 border-b">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Import Details</h2>
                    <button
                      onClick={() => setSelectedHistoryItem(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <div className="flex-1 min-h-0 p-4 sm:p-6 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                      {/* <div>
                        <label className="text-sm font-medium text-gray-700">Import ID</label>
                        <p className="text-gray-900 mt-1">{selectedHistoryItem._id}</p>
                      </div> */}
                      <div>
                        <label className="text-sm font-medium text-gray-700">Import Date</label>
                        <p className="text-gray-900 mt-1">{new Date(selectedHistoryItem.import_date).toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Imported By</label>
                        <p className="text-gray-900 mt-1">{selectedHistoryItem.imported_by}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Status</label>
                        <p className="text-gray-900 mt-1">{selectedHistoryItem.status}</p>
                      </div>
                       <div>
                        <label className="text-sm font-medium text-gray-700">Total Records</label>
                        <p className="text-gray-900 mt-1">{selectedHistoryItem.total_records}</p>
                      </div>
                    </div>

                    <div className="border-t pt-4 sm:pt-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Import Results</h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {selectedHistoryItem.results && selectedHistoryItem.results.length > 0 ? selectedHistoryItem.results.map((result, idx) => (
                          <div
                            key={idx}
                            className={`p-3 sm:p-4 rounded-lg border ${
                              result.status === 'success'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="text-xs sm:text-sm font-medium text-gray-700">Row {result.row_number}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                    {result.type}
                                  </span>
                                </div>
                                <p className="text-xs sm:text-sm text-gray-900 mb-1 break-words">
                                  <span className="font-medium">Identifier:</span> {result.identifier}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-900 mb-1 break-words">
                                  <span className="font-medium">Course:</span> {result.course_slug}
                                </p>
                                {result.error && (
                                  <p className="text-xs sm:text-sm text-red-700 mt-2 break-words">
                                    <span className="font-medium">Error:</span> {result.error}
                                  </p>
                                )}
                              </div>
                              {result.status === 'success' ? (
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No import results available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 flex items-center justify-end p-4 sm:p-6 border-t bg-gray-50">
                    <button
                      onClick={() => setSelectedHistoryItem(null)}
                      className="px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors text-sm sm:text-base"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Course Details Modal */}
      <Modal
        isOpen={!!selectedCoursesModal}
        onClose={() => setSelectedCoursesModal(null)}
        title="Assigned Courses"
        subtitle={selectedCoursesModal ? `${selectedCoursesModal.courses.length} course${selectedCoursesModal.courses.length !== 1 ? 's' : ''} assigned` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedCoursesModal(null)}
              className="px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedCoursesModal && (
          <div className="space-y-3">
            {selectedCoursesModal.courses.map((course, idx) => (
              <div key={course._id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-brand-indigo-100 text-brand-indigo-600 rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{course.title}</h3>
                  {/* <p className="text-xs text-gray-500 mt-1">Course ID: {course._id}</p> */}
                </div>
                <BookOpen className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Assigned Users/Groups/Roles Modal */}
      <Modal
        isOpen={!!selectedAssignedUsersModal}
        onClose={() => setSelectedAssignedUsersModal(null)}
        title={
          selectedAssignedUsersModal?.type === 'USER' ? 'Assigned Users' :
          selectedAssignedUsersModal?.type === 'GROUP' ? 'Assigned Departments' :
          'Assigned Roles'
        }
        subtitle={
          selectedAssignedUsersModal?.type === 'USER'
            ? `${selectedAssignedUsersModal?.users?.length || 0} user${(selectedAssignedUsersModal?.users?.length || 0) !== 1 ? 's' : ''} assigned`
            : selectedAssignedUsersModal?.type === 'GROUP'
            ? `${selectedAssignedUsersModal?.groups?.length || 0} department${(selectedAssignedUsersModal?.groups?.length || 0) !== 1 ? 's' : ''} assigned`
            : `${selectedAssignedUsersModal?.roles?.length || 0} role${(selectedAssignedUsersModal?.roles?.length || 0) !== 1 ? 's' : ''} assigned`
        }
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedAssignedUsersModal(null)}
              className="px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedAssignedUsersModal && (
          <div className="space-y-3">
            {/* Users List */}
            {selectedAssignedUsersModal.type === 'USER' && selectedAssignedUsersModal.users?.map((user) => (
              <div key={user._id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-indigo-500 to-brand-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.name ? user.name.split(' ').map(n => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                </div>
                <User className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Groups List */}
            {selectedAssignedUsersModal.type === 'GROUP' && selectedAssignedUsersModal.groups?.map((group) => (
              <div key={group.group_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.group_name}</h3>
                  <p className="text-xs text-gray-500">{group.user_count} members</p>
                </div>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Roles List */}
            {selectedAssignedUsersModal.type === 'ROLE' && selectedAssignedUsersModal.roles?.map((role) => (
              <div key={role.role_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.role_name}</h3>
                  <p className="text-xs text-gray-500">{role.user_count} users</p>
                </div>
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Skipped Users/Groups/Roles Modal */}
      <Modal
        isOpen={!!selectedSkippedUsersModal}
        onClose={() => setSelectedSkippedUsersModal(null)}
        title="Skipped Assignments"
        subtitle={`${(selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)} item${((selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)) !== 1 ? 's' : ''} skipped due to existing assignments`}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedSkippedUsersModal(null)}
              className="px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedSkippedUsersModal && (
          <div className="space-y-3">
            {/* Skipped Users List */}
            {selectedSkippedUsersModal.skipped_users?.map((user: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                  <p className="text-xs text-red-600 mt-1">{user.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}

            {/* Skipped Groups List */}
            {selectedSkippedUsersModal.skipped_groups?.map((group: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</h3>
                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                  <p className="text-xs text-blue-600 mt-1">{group.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}

            {/* Skipped Roles List */}
            {selectedSkippedUsersModal.skipped_roles?.map((role: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</h3>
                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                  <p className="text-xs text-blue-600 mt-1">{role.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Revoke Assignment Confirmation Modal */}
      <Modal
        isOpen={!!revokeModal}
        onClose={() => !revokeLoading && setRevokeModal(null)}
        title="Revoke Assignment"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setRevokeModal(null)}
              disabled={revokeLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeAssignment}
              disabled={revokeLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {revokeLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Revoking...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Revoke Assignment
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              This action cannot be undone. All related data will be permanently deleted.
            </p>
          </div>
          <p className="text-sm text-gray-700">
            Are you sure you want to revoke the {revokeModal?.type === 'GROUP' ? 'department' : 'role'} assignment for <span className="font-semibold text-gray-900">{revokeModal?.name}</span>?
          </p>
          <div className="text-sm text-gray-600 space-y-1">
            <p>The following will be deleted:</p>
            <ul className="list-disc list-inside text-gray-500 space-y-0.5">
              <li>Course tracking records (except LP-linked ones)</li>
              <li>Assessment attempts for affected users</li>
              <li>Issued course certificates for affected users</li>
              <li>The assignment record itself</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
}
